import { useEffect, useState } from 'react';
import { Gauge, RefreshCw, KeyRound, Fuel, Loader2 } from 'lucide-react';
import { StitekPlochy, CisloPlochy, RadekPlochy, Tecka, TlacitkoPlochy, barvyPlochy, fmtKm, fmtCas } from './PlochaUI';

// Na tmavém světlé odstíny, na bílém tmavší — světle zelená a žlutá na bílé
// skoro nejdou přečíst.
const ZNAMKA_BARVA_TMAVY = { 1: '#34D399', 2: '#34D399', 3: '#FBBF24', 4: '#F97316', 5: '#F87171' };
const ZNAMKA_BARVA_SVETLY = { 1: '#059669', 2: '#059669', 3: '#B45309', 4: '#C2410C', 5: '#DC2626' };
const barvyZnamek = (isDark) => (isDark ? ZNAMKA_BARVA_TMAVY : ZNAMKA_BARVA_SVETLY);
const ZNAMKA_SLOVY = { 1: 'výborný', 2: 'chvalitebný', 3: 'dobrý', 4: 'dostatečný', 5: 'nedostatečný' };

/** Barva procenta — zelená od 90, oranžová od 75, pod tím červená. */
const barvaProcent = (p, isDark) => {
    if (p == null) return null;
    const z = barvyZnamek(isDark);
    return p >= 90 ? z[1] : p >= 75 ? z[3] : z[5];
};

const sklonujJizdy = (n) => (n === 1 ? 'jízda' : n >= 2 && n <= 4 ? 'jízdy' : 'jízd');

/**
 * Kolik jízd ukázat, než se seznam sbalí. Traccar dělí jízdu u každé
 * zastávky, takže celodenní rozvoz má klidně 60 jízd — celý seznam by
 * vytlačil všechno ostatní.
 */
const JIZD_NAHLED = 6;

/**
 * Plocha „Já za volantem" — pod kartou cockpitu, když řidič klepne na dlaždici
 * Auto. Řidičův pohled na jeho dnešek, ne technický list vozidla: kolik najel,
 * jak drží sliby zákazníkům, jak se mu jezdí a kudy dnes jel. K tomu výměna
 * a uvolnění auta.
 *
 * Čísla jdou z `/api/me/za-volantem` a jsou tatáž jako v Přehledu (do 60 min,
 * slib, měsíční známka) a v PVD na POSu (dnešní známka) — řidič je tam už vidí
 * a dvě různá čísla o témže by byla první věc, které si všimne.
 *
 * Obnovuje se po minutě, dokud je vidět. Déle tu stejně nikdo nezůstane —
 * plocha se po chvíli bez dotyku sama vrátí k práci (`PracovniPlocha`).
 */
export default function ZaVolantemPanel({ vozidlo, maPokladnu = false, onVymenitAuto, onUvolnitAuto, onNatankovat, isDark }) {
    const [data, setData] = useState(null);
    const [chyba, setChyba] = useState(false);
    const [potvrzeni, setPotvrzeni] = useState(false);
    const [uvolnuji, setUvolnuji] = useState(false);
    const [vsechnyJizdy, setVsechnyJizdy] = useState(false);
    const b = barvyPlochy(isDark);
    const modra = isDark ? '#60A5FA' : '#2563EB';

    useEffect(() => {
        let zivy = true;
        const nacti = () => fetch('/api/me/za-volantem')
            .then(r => (r.ok ? r.json() : Promise.reject()))
            .then(d => { if (zivy) { setData(d); setChyba(false); } })
            .catch(() => { if (zivy) setChyba(true); });
        nacti();
        const iv = setInterval(nacti, 60000);
        return () => { zivy = false; clearInterval(iv); };
    }, []);

    const auto = data?.vozidlo || vozidlo;
    const dnes = data?.dnes;
    const kpi = data?.kpi;
    const zn = data?.znamka;

    const uvolnit = async () => {
        setUvolnuji(true);
        try {
            await onUvolnitAuto?.();
        } finally {
            setUvolnuji(false);
            setPotvrzeni(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 12px 12px' }}>
            <StitekPlochy Ikona={Gauge} barva={modra} isDark={isDark}
                vpravo={auto ? [auto.spz, auto.model].filter(Boolean).join(' · ') : null}>
                Já za volantem · dnes
            </StitekPlochy>

            {!data && !chyba && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: b.tlumena, padding: '8px 2px' }}>
                    <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Načítám tvůj dnešek…
                </div>
            )}
            {chyba && !data && (
                <div style={{ fontSize: 13, color: b.tlumena, padding: '8px 2px' }}>
                    Nepodařilo se načíst. Zkusím to znovu za minutu.
                </div>
            )}

            {data && (
                <>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <CisloPlochy isDark={isDark}
                            hodnota={fmtKm(dnes?.km ?? 0)}
                            popisek="najeto"
                            doplnek={`${dnes?.pocet_jizd ?? 0} ${sklonujJizdy(dnes?.pocet_jizd ?? 0)}${dnes?.jizda_probiha ? ' · jedeš' : ''}`}
                        />
                        <CisloPlochy isDark={isDark}
                            hodnota={kpi?.do60 != null ? `${kpi.do60} %` : '—'}
                            popisek="do 60 minut"
                            doplnek={kpi?.do60_z ? `${kpi.do60_ok} z ${kpi.do60_z}` : 'zatím nic'}
                            barva={barvaProcent(kpi?.do60, isDark)}
                        />
                        <CisloPlochy isDark={isDark}
                            hodnota={kpi?.slib != null ? `${kpi.slib} %` : '—'}
                            popisek="podle slibu"
                            doplnek={kpi?.slib_z ? `${kpi.slib_ok} z ${kpi.slib_z}` : 'zatím nic'}
                            barva={barvaProcent(kpi?.slib, isDark)}
                        />
                    </div>

                    <Znamka zn={zn} isDark={isDark} />

                    <StitekPlochy barva={b.tlumena} isDark={isDark}>Dnešní jízdy</StitekPlochy>
                    {data.jizdy?.length ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                            {(vsechnyJizdy ? data.jizdy : data.jizdy.slice(0, JIZD_NAHLED)).map(j => (
                                <RadekPlochy key={j.id} isDark={isDark}
                                    znacka={<Tecka barva={j.probiha ? modra : (barvyZnamek(isDark)[j.znamka] || (isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.18)'))} />}
                                    hlavni={j.probiha ? `od ${fmtCas(j.od)} · jedeš` : `${fmtCas(j.od)}–${fmtCas(j.do)}`}
                                    vpravo={j.km != null ? fmtKm(j.km) : null}
                                    vpravoPod={j.min != null ? `${j.min} min` : null}
                                />
                            ))}
                            {data.jizdy.length > JIZD_NAHLED && (
                                <button type="button" onClick={() => setVsechnyJizdy(v => !v)}
                                    style={{
                                        alignSelf: 'flex-start', padding: '6px 2px', border: 'none', background: 'none',
                                        fontFamily: 'inherit', fontSize: 12.5, fontWeight: 650, color: modra, cursor: 'pointer',
                                    }}>
                                    {vsechnyJizdy ? 'Ukázat jen posledních ' + JIZD_NAHLED : `Ukázat všech ${data.jizdy.length} jízd`}
                                </button>
                            )}
                        </div>
                    ) : (
                        <div style={{ fontSize: 13, color: b.tlumena, padding: '2px 2px 4px' }}>
                            Dnes zatím žádná jízda.
                        </div>
                    )}
                </>
            )}

            {/* Akce s autem. Uvolnění jde kdykoli během směny, ale ptá se —
                uprostřed rozvozu by omylem zmáčknuté tlačítko nechalo řidiče
                bez auta. */}
            {potvrzeni ? (
                <div style={{
                    display: 'flex', flexDirection: 'column', gap: 8, padding: 12, borderRadius: 12,
                    background: isDark ? 'rgba(248,113,113,0.08)' : 'rgba(248,113,113,0.07)',
                    border: '1px solid rgba(248,113,113,0.35)',
                }}>
                    <span style={{ fontSize: 13.5, fontWeight: 650, color: b.primarni }}>
                        Uvolnit {auto?.spz || 'auto'}? Pak ho může převzít někdo jiný.
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <TlacitkoPlochy isDark={isDark} onClick={() => setPotvrzeni(false)} disabled={uvolnuji}>Zpět</TlacitkoPlochy>
                        <TlacitkoPlochy isDark={isDark} Ikona={uvolnuji ? Loader2 : KeyRound} varovani onClick={uvolnit} disabled={uvolnuji}>
                            {uvolnuji ? 'Uvolňuji…' : 'Uvolnit auto'}
                        </TlacitkoPlochy>
                    </div>
                </div>
            ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                    <TlacitkoPlochy isDark={isDark} Ikona={RefreshCw} onClick={onVymenitAuto}>Vyměnit auto</TlacitkoPlochy>
                    <TlacitkoPlochy isDark={isDark} Ikona={KeyRound} varovani onClick={() => setPotvrzeni(true)}>Uvolnit auto</TlacitkoPlochy>
                </div>
            )}

            {/* Bez pokladny nemá řidič kde nafotit účtenku — tankování mu
                zůstává tady. S pokladnou jde každá účtenka přes „Vyfoť
                účtenku" a tankování se pozná samo. */}
            {!maPokladnu && onNatankovat && (
                <TlacitkoPlochy isDark={isDark} Ikona={Fuel} onClick={onNatankovat}>Natankovat</TlacitkoPlochy>
            )}
        </div>
    );
}

/**
 * Známka — velká dnešní (stejné číslo jako v PVD), vedle rada a měsíční pro
 * srovnání. Dnešní se ukáže až od `dnes_min_km`; do té doby říká, kolik zbývá.
 */
function Znamka({ zn, isDark }) {
    const b = barvyPlochy(isDark);
    const dnes = zn?.dnes ?? null;
    const barva = dnes ? barvyZnamek(isDark)[dnes] : (isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.3)');
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '11px 12px', borderRadius: 12,
            background: b.deska, border: `1px solid ${b.okraj}`, boxShadow: b.hloubka,
        }}>
            <span style={{
                width: 46, height: 46, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, fontWeight: 800, color: barva,
                background: `${dnes ? barva : '#888888'}22`, border: `2px solid ${barva}`,
            }}>{dnes ?? '–'}</span>
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: b.primarni }}>
                    Dnešní známka{dnes ? ` · ${ZNAMKA_SLOVY[dnes]}` : ''}
                </span>
                <span style={{ fontSize: 12.5, color: b.tlumena, lineHeight: 1.35 }}>{zn?.rada}</span>
            </span>
            {zn?.mesic != null && (
                <span style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: 17, fontWeight: 800, color: barvyZnamek(isDark)[zn.mesic] || b.primarni }}>{zn.mesic}</span>
                    <span style={{ fontSize: 10.5, color: b.slaba }}>{zn.mesic_nazev}</span>
                </span>
            )}
        </div>
    );
}
